import { useEffect, useState, useMemo, useCallback } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { Box, Typography, Collapse, IconButton } from '@mui/material';
import { useQuery, useInfiniteQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import dayjs from 'dayjs';

import LoadingWrapper from 'src/components/Loading/LoadingWrapper';
import InfinityScroll from 'src/components/InfinityScroll';
import FeedFilter from 'src/widgets/FeedFilter';
import StatsBlock from 'src/widgets/StatsBlock';
// import ProjectsTabs from 'src/widgets/ProjectsTabs';
import { LayoutPaper, DesktopLayout } from 'src/layout';
import useIsMobileSize from 'src/hooks/useIsMobileSize';
import useKeywordsStore from 'src/stores/keywords';
import useMessageStore from 'src/stores/messages';
import useChatsStore from 'src/stores/chats';
import useFilterStore from 'src/stores/filter';
import { FILTER_IDS_MESSAGE_DATE } from 'src/widgets/FeedFilter/utils';
import { ROUTES_URL } from 'src/constants/routes';

import Empty from 'src/components/Empty';

import Loader from 'src/components/Loading/Loader';

import Title from 'src/components/Title';

import { MonitoringStartIcon } from 'src/icons/MonitoringStartIcon';
import Bookmark from 'src/icons/Bookmark';
import { useAIFilter } from 'src/providers/AIFilterProvider';
import * as XLSX from 'xlsx';
import Checkbox from '@mui/material/Checkbox';
import Snackbar from '@mui/material/Snackbar';
import Button from '@mui/material/Button';
import Slide from '@mui/material/Slide';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';

import useUserStore from 'src/stores/user';
import { useRef } from 'react';
import MonitoringSuccessModal from 'src/components/MonitoringSuccessModal';
import useSessionStore from 'src/stores/session';

import Card from './components/Card';
import ArticlesGroup from './components/ArticlesGroup';

import type { IMessage } from 'src/types/Message';

const Feed = () => {
    const { enqueueSnackbar } = useSnackbar();
    const { fetchMessages, messages, clear, count } = useMessageStore(({ fetchMessages, messages, count, clear }) => ({
        fetchMessages,
        messages,
        clear,
        count,
    }));

    const { pathname } = useLocation();
    const {
        date = [],
        bundles,
        keywords,
        privateChats,
        locations,
        cities,
        streams,
        leadFeedback,
        clearFilter,
    } = useFilterStore(
        ({ date, bundles, keywords, privateChats, locations, cities, streams, leadFeedback, clearFilter }) => ({
            date,
            bundles,
            keywords,
            privateChats,
            locations,
            cities,
            streams,
            leadFeedback,
            clearFilter,
        })
    );

    const { fetchKeywords } = useKeywordsStore(({ fetchKeywords }) => ({ fetchKeywords }));
    const { fetchChats } = useChatsStore(({ fetchChats }) => ({ fetchChats }));
    const isMobile = useIsMobileSize();

    // Состояние выбранных сообщений
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    // WebSocket и newMessages удалены - не используются в продакшене
    const [showAnalytics, setShowAnalytics] = useState(false);
    const [isAnalyticsExpanded, setIsAnalyticsExpanded] = useState(false);
    const [isFavoriteMode, setIsFavoriteMode] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [deletingIds, setDeletingIds] = useState<Set<number>>(new Set()); // Set ID удаляемых сообщений
    // WebSocket отключен - не используется в продакшене
    // const wsRef = useRef<WebSocket | null>(null);
    const history = useHistory();

    const isFavoriteOnly = isFavoriteMode;

    const current_subscription = useUserStore(({ current_subscription }) => current_subscription);
    const isBusiness = current_subscription?.plan?.name === 'Бизнес';
    const isOnboardingCompleted = useUserStore(({ isOnboardingCompleted }) => isOnboardingCompleted);
    const { getUserSessionRequiredData } = useSessionStore(({ getUserSessionRequiredData }) => ({
        getUserSessionRequiredData,
    }));

    // Проверяем, пришли ли мы с успешной оплаты (первый раз после завершения онбординга)
    useEffect(() => {
        const searchParams = new URLSearchParams(history.location.search);
        const paymentSuccess = searchParams.get('payment_success');
        const onboardingCompleted = searchParams.get('onboarding_completed');

        const modalShownKey = 'monitoring_success_modal_shown';
        const wasShown = sessionStorage.getItem(modalShownKey);

        if (paymentSuccess === 'true' || onboardingCompleted === 'true') {
            // Немедленно обновляем данные пользователя после возврата с оплаты
            console.log('💰 Возврат с оплаты, обновляем данные пользователя...');
            getUserSessionRequiredData()
                .then(() => {
                    console.log('✅ Данные пользователя обновлены после оплаты');
                    // Проверяем условия для показа модалки после обновления данных
                    const updatedSubscription = useUserStore.getState().current_subscription;
                    const updatedOnboarding = useUserStore.getState().isOnboardingCompleted;
                    if (updatedSubscription?.is_active && updatedOnboarding && !wasShown) {
                        setShowSuccessModal(true);
                        sessionStorage.setItem(modalShownKey, 'true');
                        // Убираем параметр из URL
                        searchParams.delete('payment_success');
                        searchParams.delete('onboarding_completed');
                        const newSearch = searchParams.toString();
                        const newUrl = newSearch
                            ? `${history.location.pathname}?${newSearch}`
                            : history.location.pathname;
                        history.replace(newUrl);
                    }
                })
                .catch((error) => {
                    console.error('❌ Ошибка обновления данных после оплаты:', error);
                });
        }

        // Показываем модалку только если:
        // 1. Есть параметр payment_success или onboarding_completed в URL
        // 2. Подписка активна (или есть активные streams для lead packages)
        // 3. Онбординг завершен
        // 4. Модалка еще не была показана (используем sessionStorage для одноразового показа)
        if ((paymentSuccess === 'true' || onboardingCompleted === 'true') && isOnboardingCompleted && !wasShown) {
            setShowSuccessModal(true);
            sessionStorage.setItem(modalShownKey, 'true');

            // Убираем параметр из URL
            searchParams.delete('payment_success');
            searchParams.delete('onboarding_completed');
            const newSearch = searchParams.toString();
            const newUrl = newSearch ? `${history.location.pathname}?${newSearch}` : history.location.pathname;
            history.replace(newUrl);
        }
    }, [history, current_subscription?.is_active, isOnboardingCompleted, getUserSessionRequiredData]);

    const { aiFilter, setAiFilter } = useAIFilter();

    // Мемоизируем filterSnapshot для предотвращения лишних запросов
    const filterSnapshot = useMemo(
        () =>
            JSON.stringify({
                privateChats,
                bundles,
                keywords,
                date,
                cities,
                streams,
                aiFilter,
                leadFeedback,
            }),
        [privateChats, bundles, keywords, date, cities, streams, aiFilter, leadFeedback]
    );

    // Мемоизируем filteredMessages вместо useState + useEffect
    const filteredMessages = useMemo(() => {
        const feedbackMessages =
            leadFeedback === 'all' ? messages : messages.filter((message) => message.feedback === leadFeedback);
        if (isFavoriteOnly) {
            return feedbackMessages.filter((message) => message.favorite);
        } else if (aiFilter) {
            // Явно проверяем relevance === true
            return feedbackMessages.filter((message) => message.relevance === true);
        }
        return feedbackMessages;
    }, [messages, aiFilter, isFavoriteOnly, leadFeedback]);

    const handleSelect = useCallback((id: number, checked: boolean) => {
        setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((item) => item !== id)));
    }, []);

    const handleExport = useCallback(
        (type: 'csv' | 'xlsx') => {
            if (selectedIds.length === 0) {
                setSnackbarOpen(true);
                return;
            }
            const exportMessages = filteredMessages.filter((msg) => selectedIds.includes(msg.id));
            const data = exportMessages.map((msg) => ({
                ID: msg.id,
                Дата: msg.date,
                Автор: msg.author,
                Сообщение: msg.content,
                Ссылка: msg.link,
                Чат: msg.chat_name,
            }));
            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Избранное');
            if (type === 'csv') {
                XLSX.writeFile(wb, 'favorites.csv', { bookType: 'csv' });
            } else {
                XLSX.writeFile(wb, 'favorites.xlsx', { bookType: 'xlsx' });
            }
        },
        [selectedIds, filteredMessages]
    );

    // Сброс AI фильтра при размонтировании Feed
    useEffect(() => {
        return () => {
            setAiFilter(false);
        };
    }, []);

    const { dateFrom: filterDateFrom, dateTo: filterDateTo } = useFilterStore(({ dateFrom, dateTo }) => ({
        dateFrom,
        dateTo,
    }));

    // Мемоизируем вычисление дат
    const dateFrom = useMemo(() => {
        if (date[0] === FILTER_IDS_MESSAGE_DATE.ALL) {
            return undefined;
        }

        if (date[0] === FILTER_IDS_MESSAGE_DATE.CUSTOM) {
            return filterDateFrom;
        }

        if (date[0] === FILTER_IDS_MESSAGE_DATE.WEEK) {
            return dayjs().startOf('week').format('YYYY-MM-DD');
        }

        if (date[0] === FILTER_IDS_MESSAGE_DATE.MONTH) {
            return dayjs().startOf('month').format('YYYY-MM-DD');
        }
        return undefined;
    }, [date, filterDateFrom]);

    const dateTo = useMemo(() => {
        if (date[0] === FILTER_IDS_MESSAGE_DATE.ALL) {
            return undefined;
        }

        if (date[0] === FILTER_IDS_MESSAGE_DATE.CUSTOM) {
            return filterDateTo;
        }

        if (date[0] === FILTER_IDS_MESSAGE_DATE.WEEK) {
            return dayjs().endOf('week').format('YYYY-MM-DD');
        }

        if (date[0] === FILTER_IDS_MESSAGE_DATE.MONTH) {
            return dayjs().endOf('month').format('YYYY-MM-DD');
        }
        return undefined;
    }, [date, filterDateTo]);

    useEffect(() => {
        return () => {
            clear();
            clearFilter();
        };
    }, []);

    useEffect(() => {
        clear();
    }, [filterSnapshot]);

    const { isLoading: isLoadingKeywords } = useQuery({
        queryKey: ['fetch-keywords'],
        queryFn: fetchKeywords,
        onError: () => {
            enqueueSnackbar('Ошибка получения ключевых слов', { variant: 'error' });
        },
    });

    const { isLoading: isLoadingChats } = useQuery({
        queryKey: ['fetch-chats'],
        queryFn: fetchChats,
        onError: () => {
            enqueueSnackbar('Ошибка получения чатов', { variant: 'error' });
        },
    });

    const {
        data: infiniteQueryData,
        isLoading: isLoadingMessage,
        isFetching: isFetchingMessage,
        fetchNextPage,
        hasNextPage,
    } = useInfiniteQuery({
        queryKey: ['fetch-message', filterSnapshot],
        queryFn: ({ pageParam = 0 }) => {
            return fetchMessages({
                limit: 20,
                offset: pageParam,
                bundles: (bundles || []).join(','),
                chat_ids: (privateChats || []).join(','),
                keywords: (keywords || []).join(','),
                streams: (streams || []).join(','),
                dateFrom,
                dateTo,
                is_favorite: isFavoriteOnly,
                is_relevant: aiFilter ? true : undefined,
                feedback: leadFeedback === 'all' ? undefined : leadFeedback,
            });
        },
        getNextPageParam: (lastPage, pages) => {
            const { next } = lastPage;
            return next ? pages.length * 20 : false;
        },
        onSuccess: (data) => {
            // Синхронизируем данные из useInfiniteQuery с store
            // НО не перезаписываем оптимистичные обновления при удалении
            if (data?.pages) {
                // Собираем все сообщения из всех страниц с дедупликацией по ID
                const allMessagesMap = new Map<number, IMessage>();
                data.pages.forEach((page: any) => {
                    (page.results || []).forEach((msg: IMessage) => {
                        if (!allMessagesMap.has(msg.id)) {
                            allMessagesMap.set(msg.id, msg);
                        }
                    });
                });
                const allMessages = Array.from(allMessagesMap.values());

                const currentMessages = useMessageStore.getState().messages;
                const currentIds = new Set(currentMessages.map((m) => m.id));
                const newIds = new Set(allMessages.map((m: IMessage) => m.id));

                // Проверяем, есть ли активные удаления
                const hasActiveDeletions = deletingIds.size > 0;

                // Обновляем store только если:
                // 1. Добавились новые сообщения (новые ID, которых нет в текущих)
                // 2. ИЛИ это первая загрузка (currentIds пустой)
                // НЕ обновляем, если есть активные удаления или только удалились сообщения
                const hasNewMessages = Array.from(newIds).some((id) => !currentIds.has(id));
                const isFirstLoad = currentIds.size === 0;

                // Если есть активные удаления, не обновляем store (оптимистичное обновление уже сделано)
                if (hasActiveDeletions) {
                    // Обновляем только count, если он изменился
                    const newCount = data.pages[0]?.count || 0;
                    const currentCount = useMessageStore.getState().count;
                    if (newCount !== currentCount) {
                        useMessageStore.setState({ count: newCount });
                    }
                    return;
                }

                if (isFirstLoad || hasNewMessages) {
                    // Дедуплицируем сообщения перед обновлением store
                    // Объединяем текущие сообщения с новыми, убирая дубликаты
                    const mergedMessagesMap = new Map<number, IMessage>();
                    // Сначала добавляем текущие (чтобы сохранить оптимистичные удаления)
                    currentMessages.forEach((msg) => mergedMessagesMap.set(msg.id, msg));
                    // Затем добавляем новые из запроса
                    allMessages.forEach((msg) => mergedMessagesMap.set(msg.id, msg));

                    useMessageStore.setState({
                        messages: Array.from(mergedMessagesMap.values()),
                        count: data.pages[0]?.count || 0,
                    });
                } else {
                    // Если только удалились сообщения, обновляем только count
                    useMessageStore.setState({
                        count: data.pages[0]?.count || 0,
                    });
                }
            }
        },
        onError: (error) => {
            console.error('Ошибка загрузки сообщений:', error);
            enqueueSnackbar('Ошибка загрузки сообщений. Попробуйте обновить страницу', { variant: 'error' });
        },
        retry: 2,
        retryDelay: 1000,
    });

    const isLoading = isLoadingChats || isLoadingKeywords;

    // Мемоизируем группировку сообщений по датам
    const groupedMessages = useMemo(() => {
        return filteredMessages.reduce((acc, message) => {
            const dateKey = dayjs(message.date).format('DD.MM.YYYY');

            if (!acc[dateKey]) {
                acc[dateKey] = [];
            }

            acc[dateKey].push(message);

            return acc;
        }, {} as Record<string, IMessage[]>);
    }, [filteredMessages]);

    const renderGroups = () => {
        if (isFavoriteOnly) {
            return (
                <Box
                    display="flex"
                    flexDirection="column"
                    gap="1rem"
                >
                    {filteredMessages.map((message) => (
                        <Card
                            key={message.id}
                            body={message}
                            checkbox={
                                <Checkbox
                                    checked={selectedIds.includes(message.id)}
                                    onChange={(e) => handleSelect(message.id, e.target.checked)}
                                    color="primary"
                                />
                            }
                            onDeletingStart={(id) => setDeletingIds((prev) => new Set(prev).add(id))}
                            onDeletingEnd={(id) =>
                                setDeletingIds((prev) => {
                                    const next = new Set(prev);
                                    next.delete(id);
                                    return next;
                                })
                            }
                        />
                    ))}
                </Box>
            );
        }

        const keys = Object.keys(groupedMessages);

        return keys.map((key) => (
            <ArticlesGroup
                key={key}
                groupDate={key}
                list={groupedMessages[key]}
                onDeletingStart={(id) => setDeletingIds((prev) => new Set(prev).add(id))}
                onDeletingEnd={(id) =>
                    setDeletingIds((prev) => {
                        const next = new Set(prev);
                        next.delete(id);
                        return next;
                    })
                }
            />
        ));
    };

    // handleShowNewMessages удален - WebSocket не используется

    const renderContent = () => {
        if (isLoading) {
            return null;
        }

        if (isLoadingMessage) {
            return (
                <Loader
                    isOpen
                    isLocal
                    position="relative"
                    height={200}
                    sx={{ background: 'none' }}
                />
            );
        }
        return (
            <>
                {/* Заголовок для главной страницы */}
                {pathname === ROUTES_URL.FEED && (
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={20 / 8}
                    >
                        <Title>Лента сообщений</Title>
                        <Button
                            variant={isFavoriteMode ? 'contained' : 'outlined'}
                            color="primary"
                            onClick={() => setIsFavoriteMode(!isFavoriteMode)}
                            startIcon={<Bookmark fill={isFavoriteMode} />}
                            sx={{
                                borderRadius: 2,
                                textTransform: 'none',
                                fontWeight: 500,
                                ...(isFavoriteMode && {
                                    backgroundColor: '#fff',
                                    color: '#000',
                                    '&:hover': {
                                        backgroundColor: '#f5f5f5',
                                    },
                                    '& .MuiSvgIcon-root': {
                                        fill: '#000 !important',
                                    },
                                    '& svg': {
                                        fill: '#000 !important',
                                    },
                                    '& path': {
                                        fill: '#000 !important',
                                    },
                                }),
                            }}
                        >
                            Избранное
                        </Button>
                    </Box>
                )}

                {/* Блок аналитики - только когда НЕ в режиме избранного */}
                {pathname === ROUTES_URL.FEED && !isFavoriteMode && (
                    <Box mb={3}>
                        <StatsBlock
                            isExpanded={isAnalyticsExpanded}
                            onToggleExpanded={() => setIsAnalyticsExpanded(!isAnalyticsExpanded)}
                        />
                    </Box>
                )}

                {/* <Box
                   mx={-2}
                   style={{
                       marginTop: -2,
                   }}
                   bgcolor="background.default"
               >
                   {
                       isMobile && (
                           <ProjectsTabs
                               onChange={(id) => { }}
                               selectedIndex={0}
                               chatsList={[]}
                           />
                       )
                   }
               </Box> */}
                {isMobile && pathname === ROUTES_URL.FEED && !isFavoriteMode && <FeedFilter />}

                {/* WebSocket и новые сообщения отключены */}

                {isFavoriteOnly && filteredMessages.length < 1 && <Empty withMainButton={false} />}
                {!isFavoriteOnly && filteredMessages.length < 1 && (
                    <Empty
                        icon={<MonitoringStartIcon />}
                        title="Идёт поиск лидов"
                        text={`Обычно первые сообщения\n\n приходят в течение 24 часов`}
                        withMainButton={false}
                    />
                )}

                {filteredMessages.length > 0 && (
                    <Box mb={2}>
                        <InfinityScroll
                            isLoading={isFetchingMessage || deletingIds.size > 0}
                            fetchNext={fetchNextPage}
                            hasNextPage={!!hasNextPage && deletingIds.size === 0}
                        >
                            {renderGroups()}
                        </InfinityScroll>
                    </Box>
                )}
                <Snackbar
                    open={snackbarOpen}
                    autoHideDuration={2500}
                    onClose={() => setSnackbarOpen(false)}
                    message="Выберите сообщения для экспорта"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                />
            </>
        );
    };

    if (!isMobile) {
        return (
            <LoadingWrapper isLoading={isLoading}>
                {isFavoriteOnly && selectedIds.length > 0 && (
                    <Slide
                        direction="up"
                        in={selectedIds.length > 0}
                        mountOnEnter
                        unmountOnExit
                    >
                        <Paper
                            elevation={8}
                            sx={{
                                position: 'fixed',
                                left: 0,
                                right: 0,
                                bottom: 0,
                                zIndex: 1301,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 3,
                                py: 2,
                                bgcolor: 'background.paper',
                            }}
                        >
                            <Typography variant="body1">
                                Выбрано {selectedIds.length} из {filteredMessages.length} сообщений
                            </Typography>
                            <Box
                                display="flex"
                                gap={1}
                            >
                                <Button
                                    variant="text"
                                    color="primary"
                                    onClick={() => setSelectedIds(filteredMessages.map((m) => m.id))}
                                    disabled={selectedIds.length === filteredMessages.length}
                                >
                                    Выбрать все
                                </Button>
                                <Button
                                    variant="text"
                                    color="secondary"
                                    onClick={() => setSelectedIds([])}
                                    disabled={selectedIds.length === 0}
                                >
                                    Очистить выбор
                                </Button>
                                <Tooltip
                                    title={!isBusiness ? 'Функция экспорта доступна только для тарифов: "Бизнес"' : ''}
                                    arrow
                                >
                                    <span>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => handleExport('csv')}
                                            disabled={!isBusiness}
                                        >
                                            Экспорт CSV
                                        </Button>
                                    </span>
                                </Tooltip>
                                <Tooltip
                                    title={!isBusiness ? 'Функция экспорта доступна только для тарифов: "Бизнес"' : ''}
                                    arrow
                                >
                                    <span>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => handleExport('xlsx')}
                                            disabled={!isBusiness}
                                        >
                                            Экспорт Excel
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Box>
                        </Paper>
                    </Slide>
                )}
                <DesktopLayout>{renderContent()}</DesktopLayout>
            </LoadingWrapper>
        );
    }

    return (
        <LoadingWrapper isLoading={isLoading}>
            {isFavoriteOnly && selectedIds.length > 0 && (
                <Slide
                    direction="up"
                    in={selectedIds.length > 0}
                    mountOnEnter
                    unmountOnExit
                >
                    <Paper
                        elevation={8}
                        sx={{
                            position: 'fixed',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 1301,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            px: 3,
                            py: 2,
                            bgcolor: 'background.paper',
                        }}
                    >
                        <Typography variant="body1">
                            Выбрано {selectedIds.length} из {filteredMessages.length} сообщений
                        </Typography>
                        <Box
                            display="flex"
                            gap={1}
                        >
                            <Button
                                variant="text"
                                color="primary"
                                onClick={() => setSelectedIds(filteredMessages.map((m) => m.id))}
                                disabled={selectedIds.length === filteredMessages.length}
                            >
                                Выбрать все
                            </Button>
                            <Button
                                variant="text"
                                color="secondary"
                                onClick={() => setSelectedIds([])}
                                disabled={selectedIds.length === 0}
                            >
                                Очистить выбор
                            </Button>
                            <Tooltip
                                title={!isBusiness ? 'Функция экспорта доступна только для тарифов: "Бизнес"' : ''}
                                arrow
                            >
                                <span>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => handleExport('csv')}
                                        disabled={!isBusiness}
                                    >
                                        Экспорт CSV
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip
                                title={!isBusiness ? 'Функция экспорта доступна только для тарифов: "Бизнес"' : ''}
                                arrow
                            >
                                <span>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={() => handleExport('xlsx')}
                                        disabled={!isBusiness}
                                    >
                                        Экспорт Excel
                                    </Button>
                                </span>
                            </Tooltip>
                        </Box>
                    </Paper>
                </Slide>
            )}
            <MonitoringSuccessModal
                open={showSuccessModal}
                onClose={() => setShowSuccessModal(false)}
            />
            <LayoutPaper isWhite>{renderContent()}</LayoutPaper>
        </LoadingWrapper>
    );
};

export default Feed;
